import type { AgenticLogicTemplateSourceFile } from "@intelligems/ig-types";

/**
 * The one editable file in a template rule's workspace. Everything else in the
 * tree is the template's own implementation, which the author reads to learn
 * what the configuration has to say.
 */
export const TEMPLATE_CONFIG_FILE = "template-config.json";

export interface TemplateWorkspaceFileEntry {
  name: string;
  isLocked: boolean;
}

/**
 * The workspace's file tree: the editable configuration first, then the
 * template's implementation source in the order the server returns it (entry
 * template first, its modules after).
 */
export const templateWorkspaceFiles = (
  sourceFiles: readonly AgenticLogicTemplateSourceFile[]
): TemplateWorkspaceFileEntry[] => [
  { name: TEMPLATE_CONFIG_FILE, isLocked: false },
  ...sourceFiles.map((file) => ({ name: file.path, isLocked: true })),
];

/** What the editor renders for the selected row. */
export interface TemplateWorkspaceDocument {
  /** The row actually being shown — the config file when the ask is unknown. */
  name: string;
  language: "json" | "ts";
  contents: string;
  isReadOnly: boolean;
}

/**
 * Resolves the open row to its document.
 *
 * An unknown name resolves to the configuration rather than an empty editor:
 * the source list arrives asynchronously and can change with the template, so a
 * remembered selection routinely names a file that is not (yet) in the tree.
 */
export const templateWorkspaceDocument = ({
  activeFile,
  configSource,
  sourceFiles,
}: {
  activeFile: string;
  configSource: string;
  sourceFiles: readonly AgenticLogicTemplateSourceFile[];
}): TemplateWorkspaceDocument => {
  const sourceFile = sourceFiles.find((file) => file.path === activeFile);
  if (sourceFile) {
    return {
      name: sourceFile.path,
      language: "ts",
      contents: sourceFile.contents,
      // First-party template code: readable so the author can see exactly what
      // their configuration drives, never editable from here.
      isReadOnly: true,
    };
  }

  return {
    name: TEMPLATE_CONFIG_FILE,
    language: "json",
    contents: configSource,
    // The configuration is the one row a merchant owns, and a rule is editable
    // for its whole life — retiring one deletes it rather than freezing it.
    isReadOnly: false,
  };
};
